/**
 * The page an author is writing, and the doors it holds up.
 *
 * In the full screen reader the audio and questions of a page are written in a card drawn
 * *over* that page, and the draft lives only in the card: the surface's own `Confirm` is
 * the one thing that writes a page, so a door taken while the card is open unmounts it and
 * everything written on it goes with it. The doors are not the card's own — the arrows, the
 * wheel, the keys, a flick, the thumbnails, the way out — so each of them has to ask, and
 * asking in each of them is how one of them ends up forgetting.
 *
 * Hence one hook, and one shape for every door:
 *
 *  - **`holdForDraft(door)`** answers whether the door was *held*. `true` means the caller
 *    must do nothing else: the deed it was about to perform is now the dialog's to take.
 *    `false` means take it, because there is nothing to lose;
 *  - **the deed, not the page number**, is what is kept (`heldDoor`), so a door that asks
 *    about a *step*, a *jump* or *closing the reader* is replayed exactly as it was asked
 *    for rather than re-derived from a page that may have moved in the meantime;
 *  - **`discardDraft`** closes the card and takes the held deed — once. The ref is cleared
 *    before the deed runs, so a deed that opens another dialog or moves the page cannot be
 *    taken twice;
 *  - **every hold overwrites the last.** A dialog can only be answering one question, so an
 *    earlier deed is not waiting behind it — an author who thought better of the first turn
 *    must not have it happen when they discard the second.
 *
 * What this hook deliberately does *not* own: whether the surface may write at all (the
 * caller's `onChangePageMaterial` test), where the card is drawn, and the words of the
 * dialog. It owns the page the card is on, whether that card is dirty, the deed a dialog is
 * holding, and the fact that the dialog was asked — nothing about how any of it looks.
 */
import {useCallback, useEffect, useRef, useState} from 'react'

import {type DialogControlProps} from '#/components/Dialog/types'

export type DraftHold = {
  /** The page whose card is open, if any. */
  editorPage: number | null
  /**
   * Whether that card holds something the page does not have yet.
   *
   * Reported by the card itself, because the draft never leaves it: the reader cannot ask
   * a question it does not know the answer to.
   */
  editorDirty: boolean
  /** The card's own report of that — wire it to the card's dirty signal. */
  setEditorDirty: (dirty: boolean) => void
  /** Opens the card on the page the reader is on, which starts out holding nothing new. */
  openEditor: () => void
  /** Closes the card, whether its draft was kept or not — the card is what holds it. */
  closeEditor: () => void
  /** Holds the door if a page is half-written. `true` means the caller must do nothing else. */
  holdForDraft: (door: () => void) => boolean
  /** Lets the draft go with the card, and takes the door it was holding up. */
  discardDraft: () => void
}

export function useDraftHold({
  page,
  startEditorPage,
  discardControl,
}: {
  /** The page the reader is on: the card opens on it, and is unmounted when it moves. */
  page: number
  /** A page whose card is already open when the reader mounts (the composer's *Add activity*). */
  startEditorPage?: number
  /**
   * The surface's own confirm dialog, so the asking is the app's dialog rather than a
   * second kind of question drawn by the reader.
   */
  discardControl: Pick<DialogControlProps, 'open'>
}): DraftHold {
  const [editorPage, setEditorPage] = useState<number | null>(
    startEditorPage ?? null,
  )
  const [editorDirty, setEditorDirty] = useState(false)

  /**
   * The door an unsaved page is holding up, replayed once its author lets go.
   *
   * A ref rather than state because it is a *deed* rather than something drawn — and
   * because a function handed to `useState` is read as an updater. Only `discardDraft`
   * ever reads it, and every hold overwrites it, so a deed its author thought better of
   * is never taken.
   */
  const heldDoor = useRef<(() => void) | null>(null)

  const openEditor = useCallback(() => {
    setEditorDirty(false)
    setEditorPage(page)
  }, [page])

  const closeEditor = useCallback(() => {
    setEditorPage(null)
    setEditorDirty(false)
  }, [])

  /**
   * Whether a page's unsaved material is in the way of a door about to be taken.
   *
   * The author is the only one who knows whether the words on the page were meant, and the
   * surface's job is to put the question to them, once, in the app's own dialog.
   */
  const holdForDraft = useCallback(
    (door: () => void) => {
      if (editorPage === null || !editorDirty) return false
      heldDoor.current = door
      discardControl.open()
      return true
    },
    [discardControl, editorDirty, editorPage],
  )

  const discardDraft = useCallback(() => {
    closeEditor()
    // Read and clear *before* the deed runs: a door that moves the page must not leave a
    // deed behind for the next discard to take a second time.
    const door = heldDoor.current
    heldDoor.current = null
    door?.()
  }, [closeEditor])

  /**
   * An editor the page has moved out from under is not an editor.
   *
   * Nothing should be able to move a page while the card is open, because every door asks
   * first — but the card is drawn on `editorPage === page`, and the controls that open it
   * are drawn on `editorPage === null`: an editor left behind by a page that moved anyway
   * would take the author's ways of writing the page with it, and leave them with a page
   * they could no longer add anything to.
   */
  useEffect(() => {
    if (editorPage === null || editorPage === page) return
    /*
     * A state write in an effect deliberately, rather than an open card *derived* while
     * rendering: what has to be cleared is the draft mirror, because a card whose page moved
     * has already lost its words with the unmounted card — and an author who came back to
     * that page must not be asked to answer for words that are gone. Deriving `editorPage`
     * from `page` alone would let the stale card reappear, dirty flag and all.
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one report per effect; both writes are the same repair
    setEditorPage(null)
    setEditorDirty(false)
  }, [editorPage, page])

  return {
    editorPage,
    editorDirty,
    setEditorDirty,
    openEditor,
    closeEditor,
    holdForDraft,
    discardDraft,
  }
}
